import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PRICING } from '../mock';
import { track } from '../lib/analytics';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busyId, setBusyId] = useState(null);

  const startCheckout = async (tier) => {
    if (!user) {
      // Stash the target plan + send to login.
      sessionStorage.setItem('post_login_redirect', '/pricing');
      navigate('/login?next=/pricing');
      return;
    }
    setBusyId(tier.packageId);
    track('checkout_click', { plan: tier.name, source: 'pricing' });
    try {
      const { data } = await api.post('/checkout/session', {
        package_id: tier.packageId,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Try again.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Checkout could not be started.';
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section id="pricing" className="relative border-t border-white/5">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-[10px] tracking-[0.25em] font-mono gold-text">PRICING</p>
          <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-[1.05]">
            Subscribe to the tier
            <br />
            <span className="gold-text">your strategy needs.</span>
          </h2>
          <p className="mt-6 text-zinc-400">
            Every plan starts in paper mode. Live execution unlocks only after validation.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {PRICING.map((tier) => {
            const tierSlug = tier.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            const isBusy = busyId === tier.packageId;
            return (
              <div
                key={tier.name}
                className={`relative rounded-md p-8 border bg-[#101012] flex flex-col ${
                  tier.highlight ? 'border-[#C9A24B]/60 gold-glow' : 'border-[rgba(201,162,75,0.16)]'
                }`}
              >
                {tier.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-mono tracking-widest bg-[#C9A24B] text-[#1a1407] rounded-sm">
                    MOST POPULAR
                  </span>
                )}
                {tier.badge && (
                  <span
                    className="absolute -top-3 left-6 px-3 py-1 text-[10px] font-mono tracking-widest bg-[#E8C874] text-[#1a1407] rounded-sm"
                    data-testid={`pricing-badge-${tierSlug}`}
                  >
                    {tier.badge}
                  </span>
                )}
                <div className="flex items-baseline justify-between">
                  <h3 className="font-display text-xl font-bold text-[#F1ECDF]">{tier.name}</h3>
                  <span className="text-[10px] font-mono tracking-widest text-[#A99563]">PLAN</span>
                </div>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-4xl font-display font-bold text-[#F1ECDF] font-mono">${tier.price}</span>
                  <span className="text-sm text-[#A99563]">{tier.cadence}</span>
                </div>
                <p className="mt-3 text-sm text-[#CDBE94] leading-relaxed">{tier.tagline}</p>
                <ul className="mt-6 space-y-3 pt-6 border-t border-[rgba(201,162,75,0.16)] flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13px] text-[#CDBE94]">
                      <Check size={14} className="mt-0.5 gold-text shrink-0" strokeWidth={2} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {tier.packageId ? (
                  <button
                    type="button"
                    onClick={() => startCheckout(tier)}
                    disabled={isBusy}
                    data-testid={`pricing-cta-${tierSlug}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 py-3 rounded-sm text-sm font-medium transition-colors ${
                      tier.highlight
                        ? 'bg-[#C9A24B] text-[#1a1407] hover:bg-[#E8C874]'
                        : 'border border-[rgba(201,162,75,0.4)] text-[#F1ECDF] hover:border-[#E8C874] hover:bg-[#C9A24B]/10'
                    } ${isBusy ? 'opacity-60 cursor-wait' : ''}`}
                  >
                    {isBusy ? <Loader2 size={14} className="animate-spin" /> : null}
                    {isBusy ? 'Redirecting...' : tier.cta}
                    {!isBusy && <ArrowRight size={14} />}
                  </button>
                ) : (
                  <Link
                    to="/signup"
                    data-testid={`pricing-cta-${tierSlug}`}
                    onClick={() => track('sign_up_click', { source: 'pricing', plan: tier.name })}
                    className={`mt-8 inline-flex items-center justify-center gap-2 py-3 rounded-sm text-sm font-medium transition-colors ${
                      tier.highlight
                        ? 'bg-[#C9A24B] text-[#1a1407] hover:bg-[#E8C874]'
                        : 'border border-[rgba(201,162,75,0.4)] text-[#F1ECDF] hover:border-[#E8C874] hover:bg-[#C9A24B]/10'
                    }`}
                  >
                    {tier.cta}
                    <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
